import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { safeZoneUniforms } from '../shaders/safeZone.glsl.js';
import { loadArtworkTexture } from '../artwork-texture.js';
import { vdjColor, vdjBeatChroma, VdjPost, VdjComfort, VDJ_DROP_FLASH } from './lib/vdjLook.js';
import { SceneClock } from './lib/scene-clock.js';

// Contrat du rig partagé : interdit. La mosaïque a sa propre lumière et sa caméra.
const RIG = 'forbidden';
export { RIG };

export const PROFILE = {
  id: 'facets',
  family: 'geometric',
  danceability: 0.84,
  requiresTier: 0,
  energyLevel: 0.78,
  bpmMin: 100,
  bpmMax: 160,
  movement: 0.80,
  tonality: 0.55,
  enabled: true
};

// Facets : la pochette convertie en mosaïque LOW-POLY - 600 à 1 500
// triangles de la couleur moyenne des pixels qu'ils couvrent, plus denses
// sur les détails. Lisible au repos, elle devient un objet : les triangles
// se retournent comme des tuiles, s'extrudent en prismes, s'envolent, et la
// pochette se reconstruit. Preset party de vdjLook. EDM, house, trance,
// pop, techno mélodique.
//
// LA TRIANGULATION (R17 : un Web Worker au changement de pochette, repli
// sur le fil principal) : la pochette réduite à 256² ; contours par Sobel ;
// 300 points sur une grille jitterée + jusqu'à 1 200 points tirés sur les
// contours forts (espacés par une grille d'occupation) + les coins et les
// bords ; Delaunay par Bowyer-Watson ; couleur de chaque triangle =
// moyenne des pixels couverts (rastérisation par lignes de balayage à
// 256²) ; une triangulation GROSSIÈRE (≈ 80 triangles, grille 6 x 6 +
// coins) donne à chaque triangle fin sa couleur « simplifiée » (celle du
// triangle grossier qui contient son centroïde). Résultat : des tableaux
// par triangle, chargés UNE FOIS en attributs.
//
// UN SEUL MESH, non indexé : par triangle, le dessus (3 sommets) et les
// trois flancs du prisme (6 sommets, aSide = 1, qui ne se lèvent qu'avec
// l'extrusion) ; attributs par sommet : position xy dans la mosaïque,
// centroïde, couleur, couleur de dos, couleur simplifiée, normale
// perturbée, hasards, l'arête d'axe du FLIP (ses deux extrémités), les
// barycentriques (arêtes de 1 px, R1). TOUTE l'animation est dans le
// vertex shader, pilotée par 5 enveloppes (uFlip, uExtrude, uScatter,
// uRefract, uSimplify) et quelques scalaires : aucun upload par frame.
//
// LES MOUVEMENTS, un par mesure, choisis par un automate sans répétition :
//   FLIP      vague de retournement depuis un coin : chaque triangle tourne
//             de 360° autour d'une de ses arêtes (dos visible entre 90° et
//             270°, dans la palette de dos), puis est revenu.
//   EXTRUDE   prismes de hauteur bands.low x hasard, caméra en orbite de
//             20° : relief cristallin ; les flancs plus sombres.
//   SCATTER   les triangles s'envolent (translation radiale + dérive, rotation
//             autour du centroïde), puis reviennent EXACTEMENT en place (la
//             position de repos est l'attribut, jamais intégrée).
//   REFRACT   verre facetté : normales par triangle, une lumière qui tourne,
//             éclats spéculaires et léger décalage chromatique par facette
//             (pas au tier 0).
//   SIMPLIFY  les couleurs fondent vers les 80 triangles grossiers puis
//             reviennent : de l'abstrait au lisible (les sommets ne bougent
//             pas : les glisser ouvrait des fissures).
// LA LUMIÈRE : normale par triangle légèrement perturbée, lumière
// directionnelle qui tourne lentement (±8 % au repos : R24, les couleurs
// restent celles de la pochette ; plus forte pendant les mouvements) ; sur
// chaque temps une vague de lumière balaie la mosaïque en diagonale.
// RYTHME : temps = vague + séparation RGB vdj ; mesure = mouvement suivant ;
// phrase = palette de dos (2 variantes) et arêtes visibles ou non ; montée
// = simplification progressive ; drop = SCATTER total puis reconstruction
// en un temps, flash (VdjComfort.requestFlash).
// SCÈNE (R20, 16 s) : mosaïque formée et lisible dès la première image.
// R19 : la mosaïque fait 74 % de la hauteur, centrée sur l'écran ; le masque
// safeZone suit la pochette. Caméra PRIVÉE (la partagée est balancée par le
// gestionnaire de scène), cible MSAA privée (R5), sans AA au tier 0.
// Confort (R10) : estimation = luminance moyenne de la pochette x le gain
// courant (sinon le régulateur divise le gain à chaque image).
//
// Debug : ?facetmove=flip|extrude|scatter|refract|simplify (le mouvement
// tenu à chaque mesure), ?facetcount=<n> (triangles), ?facetcover=<url>
// (une image locale à la place de la pochette : vignettes), ?facetedges=1.

const SRC = 256;
const TIER = { 0: { count: 600, refract: false, msaa: 0 }, 1: { count: 1000, refract: true, msaa: 4 }, 2: { count: 1500, refract: true, msaa: 4 } };
const MOVES = ['flip', 'extrude', 'scatter', 'refract', 'simplify'];
const HEIGHT = 2.0;                   // the mosaic's height, world units (74 % of the frame from the camera)
const SCENE_S = 16;
const SAFE_FLOOR = 0.4;
const FALLBACK = [0x00e5ff, 0xff2bd6, 0xffe14d];

function urlParam(name) {
  if (typeof location === 'undefined') return null;
  return new URLSearchParams(location.search).get(name);
}
const clamp01 = (x) => Math.max(0, Math.min(1, x));

// ------------------------------------------------------------------ the triangulation (worker)
// Input { id, w, data (RGBA bytes, w x w, v up), count }. Output { id, n, tri (Float32Array n*6: 3 xy in 0..1),
// col (n*3), coarse (n*3), ncoarse }.
function facetsWorkerSource() {
  return `
  function triangulate(w, data, count) {
    var n = w * w, lum = new Float32Array(n);
    for (var i = 0; i < n; i++) lum[i] = (0.2126 * data[i * 4] + 0.7152 * data[i * 4 + 1] + 0.0722 * data[i * 4 + 2]) / 255;
    // Sobel edges
    var edge = new Float32Array(n), emax = 1e-6;
    for (var y = 1; y < w - 1; y++) for (var x = 1; x < w - 1; x++) {
      var i0 = y * w + x;
      var gx = (lum[i0 - w + 1] + 2 * lum[i0 + 1] + lum[i0 + w + 1]) - (lum[i0 - w - 1] + 2 * lum[i0 - 1] + lum[i0 + w - 1]);
      var gy = (lum[i0 + w - 1] + 2 * lum[i0 + w] + lum[i0 + w + 1]) - (lum[i0 - w - 1] + 2 * lum[i0 - w] + lum[i0 - w + 1]);
      var e = Math.sqrt(gx * gx + gy * gy); edge[i0] = e; if (e > emax) emax = e;
    }
    var seed = 12345; var rnd = function () { seed = (seed * 16807) % 2147483647; return seed / 2147483647; };
    var pts = [];
    // corners and borders
    var B = 12;
    for (var k = 0; k <= B; k++) { var t = k / B; pts.push([t, 0], [t, 1]); if (k > 0 && k < B) pts.push([0, t], [1, t]); }
    // the jittered grid: 300 points
    var G = 17;
    for (var gy2 = 0; gy2 < G; gy2++) for (var gx2 = 0; gx2 < G; gx2++) {
      if (pts.length >= 4 * B + 300) break;
      pts.push([(gx2 + 0.15 + 0.7 * rnd()) / G, (gy2 + 0.15 + 0.7 * rnd()) / G]);
    }
    // the edge points: candidates by edge strength, an occupancy grid keeps them apart
    var want = Math.max(0, count - pts.length - 40), cell = Math.max(2, Math.floor(w / Math.sqrt(want * 1.6)));
    var occ = {}; var cand = [];
    for (var i2 = 0; i2 < n; i2++) if (edge[i2] > 0.18 * emax) cand.push(i2);
    // shuffle, then take by decreasing strength bands
    for (var s = cand.length - 1; s > 0; s--) { var j = Math.floor(rnd() * (s + 1)); var tmp = cand[s]; cand[s] = cand[j]; cand[j] = tmp; }
    cand.sort(function (a, b) { return edge[b] - edge[a]; });
    var taken = 0;
    for (var c = 0; c < cand.length && taken < want; c++) {
      var px = cand[c] % w, py = Math.floor(cand[c] / w);
      var key = Math.floor(px / cell) + ',' + Math.floor(py / cell);
      if (occ[key]) continue;
      occ[key] = 1; taken++;
      pts.push([(px + 0.5 + (rnd() - 0.5) * 0.6) / w, (py + 0.5 + (rnd() - 0.5) * 0.6) / w]);
    }
    var tri = delaunay(pts);
    var out = rasterColours(pts, tri, data, w);
    // the coarse triangulation: corners + a 6 x 6 jittered grid
    var cp = [[0, 0], [1, 0], [0, 1], [1, 1]];
    for (var cy = 0; cy < 6; cy++) for (var cx = 0; cx < 6; cx++) cp.push([(cx + 0.2 + 0.6 * rnd()) / 6, (cy + 0.2 + 0.6 * rnd()) / 6]);
    var ctri = delaunay(cp);
    var cout = rasterColours(cp, ctri, data, w);
    // each fine triangle's coarse colour: the coarse triangle holding its centroid
    var coarse = new Float32Array(tri.length * 3);
    for (var f = 0; f < tri.length; f++) {
      var a = pts[tri[f][0]], b = pts[tri[f][1]], c2 = pts[tri[f][2]];
      var mx = (a[0] + b[0] + c2[0]) / 3, my = (a[1] + b[1] + c2[1]) / 3;
      var best = 0, bd = 1e9;
      for (var g = 0; g < ctri.length; g++) {
        var p = cp[ctri[g][0]], q = cp[ctri[g][1]], r = cp[ctri[g][2]];
        if (inside(mx, my, p, q, r)) { best = g; bd = 0; break; }
        var d = Math.pow((p[0] + q[0] + r[0]) / 3 - mx, 2) + Math.pow((p[1] + q[1] + r[1]) / 3 - my, 2);
        if (d < bd) { bd = d; best = g; }
      }
      coarse[f * 3] = cout.col[best * 3]; coarse[f * 3 + 1] = cout.col[best * 3 + 1]; coarse[f * 3 + 2] = cout.col[best * 3 + 2];
    }
    var tr = new Float32Array(tri.length * 6), area = 0;
    for (var f2 = 0; f2 < tri.length; f2++) {
      for (var v = 0; v < 3; v++) { tr[f2 * 6 + v * 2] = pts[tri[f2][v]][0]; tr[f2 * 6 + v * 2 + 1] = pts[tri[f2][v]][1]; }
      var A0 = pts[tri[f2][0]], B0 = pts[tri[f2][1]], C0 = pts[tri[f2][2]];
      area += Math.abs((B0[0] - A0[0]) * (C0[1] - A0[1]) - (C0[0] - A0[0]) * (B0[1] - A0[1])) * 0.5;
    }
    return { n: tri.length, tri: tr, col: out.col, coarse: coarse, ncoarse: ctri.length, area: area, npts: pts.length };
  }
  function inside(x, y, p, q, r) {
    var d1 = (x - q[0]) * (p[1] - q[1]) - (p[0] - q[0]) * (y - q[1]);
    var d2 = (x - r[0]) * (q[1] - r[1]) - (q[0] - r[0]) * (y - r[1]);
    var d3 = (x - p[0]) * (r[1] - p[1]) - (r[0] - p[0]) * (y - p[1]);
    var neg = (d1 < 0) || (d2 < 0) || (d3 < 0), pos = (d1 > 0) || (d2 > 0) || (d3 > 0);
    return !(neg && pos);
  }
  // Bowyer-Watson with a super triangle; triangles as [i, j, k] plus a cached circumcircle
  function delaunay(pts) {
    var P = pts.slice();
    var st = [[-10, -10], [30, -10], [-10, 30]];
    var o = P.length; P.push(st[0], st[1], st[2]);
    var tris = [circ(P, [o, o + 1, o + 2])];
    // a shuffled insertion order and an epsilon incircle test: points near a pixel
    // grid are often nearly cocircular, and a sorted order left holes
    var order = []; for (var q = 0; q < o; q++) order.push(q);
    var sd = 777; var rr = function () { sd = (sd * 16807) % 2147483647; return sd / 2147483647; };
    for (var s2 = o - 1; s2 > 0; s2--) { var j2 = Math.floor(rr() * (s2 + 1)); var tq = order[s2]; order[s2] = order[j2]; order[j2] = tq; }
    for (var oi = 0; oi < o; oi++) {
      var i = order[oi];
      var p = P[i], bad = [], poly = [];
      for (var t = 0; t < tris.length; t++) {
        var T = tris[t];
        var dx = p[0] - T.cx, dy = p[1] - T.cy;
        if (dx * dx + dy * dy <= T.r2 * (1.0 + 1e-9) + 1e-14) bad.push(t);
      }
      var edges = {};
      for (var b = 0; b < bad.length; b++) {
        var v = tris[bad[b]].v;
        for (var e = 0; e < 3; e++) {
          var a = v[e], c = v[(e + 1) % 3];
          var key = a < c ? a + '_' + c : c + '_' + a;
          if (edges[key]) edges[key].n++; else edges[key] = { a: a, c: c, n: 1 };
        }
      }
      for (var b2 = bad.length - 1; b2 >= 0; b2--) tris.splice(bad[b2], 1);
      for (var k in edges) if (edges[k].n === 1) tris.push(circ(P, [edges[k].a, edges[k].c, i]));
    }
    var out = [];
    for (var t2 = 0; t2 < tris.length; t2++) { var v2 = tris[t2].v; if (v2[0] < o && v2[1] < o && v2[2] < o) out.push(v2); }
    return out;
  }
  function circ(P, v) {
    var a = P[v[0]], b = P[v[1]], c = P[v[2]];
    var d = 2 * (a[0] * (b[1] - c[1]) + b[0] * (c[1] - a[1]) + c[0] * (a[1] - b[1]));
    if (Math.abs(d) < 1e-14) return { v: v, cx: 0, cy: 0, r2: 1e30 };     // degenerate: always bad, removed at the next insertion
    var a2 = a[0] * a[0] + a[1] * a[1], b2 = b[0] * b[0] + b[1] * b[1], c2 = c[0] * c[0] + c[1] * c[1];
    var cx = (a2 * (b[1] - c[1]) + b2 * (c[1] - a[1]) + c2 * (a[1] - b[1])) / d;
    var cy = (a2 * (c[0] - b[0]) + b2 * (a[0] - c[0]) + c2 * (b[0] - a[0])) / d;
    return { v: v, cx: cx, cy: cy, r2: (a[0] - cx) * (a[0] - cx) + (a[1] - cy) * (a[1] - cy) };
  }
  // the mean colour of the pixels each triangle covers (scanline fill at w x w); an empty one samples its centroid
  function rasterColours(pts, tri, data, w) {
    var col = new Float32Array(tri.length * 3);
    for (var f = 0; f < tri.length; f++) {
      var v = [pts[tri[f][0]], pts[tri[f][1]], pts[tri[f][2]]].sort(function (p, q) { return p[1] - q[1]; });
      var r = 0, g = 0, b = 0, cnt = 0;
      var y0 = Math.max(0, Math.ceil(v[0][1] * w - 0.5)), y1 = Math.min(w - 1, Math.floor(v[2][1] * w - 0.5));
      for (var y = y0; y <= y1; y++) {
        var yy = (y + 0.5) / w;
        var xa = interp(v[0], v[2], yy), xb = yy < v[1][1] ? interp(v[0], v[1], yy) : interp(v[1], v[2], yy);
        if (xa > xb) { var t = xa; xa = xb; xb = t; }
        var x0 = Math.max(0, Math.ceil(xa * w - 0.5)), x1 = Math.min(w - 1, Math.floor(xb * w - 0.5));
        for (var x = x0; x <= x1; x++) { var i = (y * w + x) * 4; r += data[i]; g += data[i + 1]; b += data[i + 2]; cnt++; }
      }
      if (cnt === 0) {
        var mx = Math.min(w - 1, Math.max(0, Math.floor((v[0][0] + v[1][0] + v[2][0]) / 3 * w)));
        var my = Math.min(w - 1, Math.max(0, Math.floor((v[0][1] + v[1][1] + v[2][1]) / 3 * w)));
        var i2 = (my * w + mx) * 4; r = data[i2]; g = data[i2 + 1]; b = data[i2 + 2]; cnt = 1;
      }
      col[f * 3] = r / cnt / 255; col[f * 3 + 1] = g / cnt / 255; col[f * 3 + 2] = b / cnt / 255;
    }
    return { col: col };
  }
  function interp(p, q, y) { var d = q[1] - p[1]; return Math.abs(d) < 1e-9 ? p[0] : p[0] + (q[0] - p[0]) * (y - p[1]) / d; }
  self.onmessage = function (ev) {
    var m = ev.data;
    try { var t0 = performance.now(); var res = triangulate(m.w, m.data, m.count); res.id = m.id; res.ms = performance.now() - t0; self.postMessage(res, [res.tri.buffer, res.col.buffer, res.coarse.buffer]); }
    catch (e) { self.postMessage({ id: m.id, error: String(e && e.message || e) }); }
  };
  `;
}
let _worker = null, _workerFailed = false, _nextId = 0;
const _waiting = new Map();
function sharedWorker() {
  if (_worker || _workerFailed) return _worker;
  if (typeof Worker === 'undefined' || typeof Blob === 'undefined' || typeof URL === 'undefined') { _workerFailed = true; return null; }
  try {
    _worker = new Worker(URL.createObjectURL(new Blob([facetsWorkerSource()], { type: 'text/javascript' })));
    _worker.onmessage = (ev) => { const cb = _waiting.get(ev.data.id); if (cb) { _waiting.delete(ev.data.id); cb(ev.data); } };
    _worker.onerror = () => { for (const [id, cb] of _waiting) cb({ id, error: 'worker' }); _waiting.clear(); };
  } catch (e) { _workerFailed = true; _worker = null; }
  return _worker;
}
function triangulateOnMainThread(w, data, count) {
  const fn = new Function(facetsWorkerSource().replace(/self\.onmessage[\s\S]*$/, '') + '\nreturn triangulate;')();
  return fn(w, data, count);
}

// ------------------------------------------------------------------ shaders
const facetVertex = `
  precision highp float;
  attribute vec2 aCentroid, aAxisA, aAxisB;
  attribute vec3 aColor, aBack, aCoarse, aNormal, aRand, aBary;
  attribute float aSide;                      // 0 top face, 1 prism side (lifted only by the extrusion)
  uniform float uFlip, uExtrude, uScatter, uRefract, uSimplify, uRebuild, uLow, uTime, uWidth;
  uniform vec2 uFlipFrom;
  varying vec3 vCol, vBack, vN, vBary, vView;
  varying float vBackFace, vSide, vDetail;
  mat3 rotAxis(vec3 ax, float an) {
    float c = cos(an), s = sin(an), k = 1.0 - c;
    return mat3(c + ax.x * ax.x * k, ax.y * ax.x * k + ax.z * s, ax.z * ax.x * k - ax.y * s,
                ax.x * ax.y * k - ax.z * s, c + ax.y * ax.y * k, ax.z * ax.y * k + ax.x * s,
                ax.x * ax.z * k + ax.y * s, ax.y * ax.z * k - ax.x * s, c + ax.z * ax.z * k);
  }
  void main() {
    // the mosaic plane: uv 0..1 -> world, centred, HEIGHT high
    vec2 scale = vec2(uWidth, ${HEIGHT.toFixed(2)});
    vec2 c0 = (aCentroid - 0.5) * scale;
    vec3 P = vec3((position.xy - 0.5) * scale, 0.0);
    vec3 N = normalize(vec3(aNormal.xy, 1.0));
    float backFace = 0.0;
    // SIMPLIFY morphs the colours only: sliding the vertices toward their centroid opened cracks (R: joined tiles)
    // EXTRUDE: the prism's height; the sides' top edge rises with it
    float h = uExtrude * (0.08 + 0.35 * uLow) * (0.35 + 0.65 * aRand.x) * ${HEIGHT.toFixed(2)};
    if (aSide > 0.5) { P.z = position.z * h; }
    else P.z = h;
    // FLIP: a wave from a corner; each triangle turns 360 deg about one of its edges
    float dist = length(aCentroid - uFlipFrom) / 1.42;
    float ft = clamp((uFlip * 1.5 - dist) / 0.5, 0.0, 1.0);
    if (ft > 0.0 && ft < 1.0) {
      float ang = ft * 6.2831853;
      vec3 A = vec3((aAxisA - 0.5) * scale, 0.0), B = vec3((aAxisB - 0.5) * scale, 0.0);
      vec3 ax = normalize(B - A);
      mat3 R = rotAxis(ax, ang);
      P = A + R * (P - A);
      N = R * N;
      backFace = step(0.25, ft) * step(ft, 0.75);
    }
    // SCATTER: fly away (radial + drift), spin about the centroid; the rest pose is the attribute
    if (uScatter > 0.0) {
      vec3 dir = normalize(vec3(c0 * 0.6 + (aRand.yz - 0.5) * 1.5, 0.6 + aRand.x));
      vec3 rel = P - vec3(c0, 0.0);
      mat3 R2 = rotAxis(normalize(aRand - 0.5 + vec3(0.0, 0.0, 0.2)), uScatter * (2.0 + 4.0 * aRand.z));
      P = vec3(c0, 0.0) + R2 * rel + dir * uScatter * (1.2 + 1.5 * aRand.y) * ${HEIGHT.toFixed(2)};
      N = R2 * N;
    }
    // the drop's rebuild: the same flight, run backward by uRebuild (1 = in place)
    if (uRebuild < 1.0) {
      float k = 1.0 - uRebuild;
      vec3 dir2 = normalize(vec3(c0 * 0.6 + (aRand.yz - 0.5) * 1.5, 0.6 + aRand.x));
      P += dir2 * k * (2.0 + 2.0 * aRand.y) * ${HEIGHT.toFixed(2)};
    }
    vec4 mv = modelViewMatrix * vec4(P, 1.0);
    vN = normalize(normalMatrix * N);
    vView = normalize(-mv.xyz);
    vCol = mix(aColor, aCoarse, uSimplify);
    vBack = aBack; vBary = aBary; vBackFace = backFace; vSide = aSide; vDetail = aRand.x;
    gl_Position = projectionMatrix * mv;
  }
`;
const facetFragment = `
  precision highp float;
  uniform vec3 uLightDir;
  uniform float uLightK, uRefract, uEdges, uWave, uFlash, uExposure, uTime, uMoveK, uDebug;
  varying vec3 vCol, vBack, vN, vBary, vView;
  varying float vBackFace, vSide, vDetail;
  void main() {
    if (uDebug > 0.5) { gl_FragColor = uDebug < 1.5 ? vec4(vCol, 1.0) : vec4(vSide, 1.0 - vSide, vBackFace, 1.0); return; }
    vec3 n = normalize(vN);
    if (!gl_FrontFacing) n = -n;
    vec3 base = mix(vCol, vBack, vBackFace);
    if (vSide > 0.5) base *= 0.55;                            // the prisms' sides, darker
    // the light: a slow directional light; faint at rest (R24), strong in the moves
    float diff = max(dot(n, uLightDir), 0.0);
    float lightK = 0.08 + 0.55 * uMoveK;
    vec3 c = base * (1.0 - lightK + lightK * (0.5 + diff));
    // REFRACT: faceted glass - sharp specular glints, a chromatic shift per facet
    vec3 hv = normalize(uLightDir + vView);
    float spec = pow(max(dot(n, hv), 0.0), 60.0);
    c += uRefract * (spec * 0.7 + 0.08) * mix(vec3(1.0), vec3(1.1, 0.95, 1.2), vDetail);
    // the beat's light wave: a diagonal band across the mosaic
    float wv = exp(-pow((vBary.x * 0.0 + gl_FragCoord.x / 1000.0 + gl_FragCoord.y / 1000.0) * 1.1 - uWave, 2.0) * 60.0);
    c += wv * 0.25 * (0.3 + 0.7 * uMoveK + 0.3);
    // the edges: 1 px lighter lines along the triangles (R1), by the barycentrics' pixel distance
    float dEdge = min(vBary.x, min(vBary.y, vBary.z));
    float fw = max(fwidth(dEdge), 1e-5);
    float edge = 1.0 - smoothstep(0.5, 1.5, dEdge / fw);
    c = mix(c, c * 1.35 + 0.08, edge * uEdges * (1.0 - vSide));
    c = mix(c, vec3(1.0), uFlash * 0.5);
    gl_FragColor = vec4(c * uExposure, 1.0);
  }
`;
const quadVertex = `
  varying vec2 vUv;
  void main() { vUv = uv; gl_Position = vec4(position.xy * 2.0, 0.0, 1.0); }
`;
const displayFragment = `
  precision highp float;
  uniform sampler2D uTex;
  uniform float uAspect, uTime;
  uniform vec2 uSafeCenter, uSafeHalfSize;
  uniform float uSafeFeather;
  varying vec2 vUv;
  float hash2(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
  void main() {
    vec3 col = texture2D(uTex, vUv).rgb;
    float L = dot(col, vec3(0.2126, 0.7152, 0.0722));
    float k = L > 0.6 ? (0.6 + 0.16 * (1.0 - exp(-(L - 0.6) / 0.16))) / L : 1.0;
    col *= k;
    vec2 s2 = (vUv * 2.0 - 1.0) * vec2(uAspect, 1.0);
    vec2 q = abs(s2 - uSafeCenter) - uSafeHalfSize;
    float sd = length(max(q, 0.0)) + min(max(q.x, q.y), 0.0);
    col *= mix(${SAFE_FLOOR.toFixed(2)}, 1.0, smoothstep(0.0, uSafeFeather, sd));
    col += (hash2(gl_FragCoord.xy + fract(uTime)) - 0.5) / 255.0;
    gl_FragColor = vec4(max(col, 0.0), 1.0);
  }
`;

export class FacetsVisualizer extends Visualizer {
  constructor(options) {
    super();
    const o = options || {};
    this._tier = o.qualityTier != null ? o.qualityTier : 1;
    this._layout = TIER[this._tier] || TIER[1];
    const fc = parseInt(urlParam('facetcount'), 10);
    this._count = Number.isFinite(fc) && fc > 50 ? Math.min(2000, fc) : this._layout.count;
    const fm = urlParam('facetmove'); this._forceMove = MOVES.indexOf(fm) >= 0 ? fm : null;
    this._forceEdges = urlParam('facetedges') === '1';
    this._debugCover = urlParam('facetcover');
    this._post = new VdjPost({ bloomStrength: 0.35, bloomThreshold: 0.78, grain: 0.0, vignette: 0.35, toneMapping: THREE.NoToneMapping, toneMappingExposure: 1 });
    this._comfort = new VdjComfort();
    this._clock = new SceneClock({ period: SCENE_S, start: 'random' });
  }

  get postProcessing() { return this._post.value; }
  _rnd() { this._seed = (this._seed * 16807) % 2147483647; return this._seed / 2147483647; }

  init(scene) {
    this._scene = scene; this._seed = 4321;
    sharedWorker();
    this._camera = new THREE.PerspectiveCamera(32, 16 / 9, 0.1, 100);
    this._u = {
      uFlip: { value: 0 }, uExtrude: { value: 0 }, uScatter: { value: 0 }, uRefract: { value: 0 }, uSimplify: { value: 0 }, uRebuild: { value: 1 },
      uLow: { value: 0.3 }, uTime: { value: 0 }, uWidth: { value: HEIGHT }, uFlipFrom: { value: new THREE.Vector2(0, 0) },
      uLightDir: { value: new THREE.Vector3(0.3, 0.5, 0.8).normalize() }, uLightK: { value: 1 }, uEdges: { value: 0 }, uWave: { value: -1 }, uFlash: { value: 0 },
      uExposure: { value: 1 }, uMoveK: { value: 0 }, uDebug: { value: parseInt(urlParam('facetdebug'), 10) || 0 }
    };
    this._mat = new THREE.ShaderMaterial({ uniforms: this._u, vertexShader: facetVertex, fragmentShader: facetFragment, side: THREE.DoubleSide });
    this._geo = null; this._mesh = null;
    this._priv = new THREE.Scene();
    this._rt = null;
    this._du = { uTex: { value: null }, uAspect: safeZoneUniforms.uAspect, uTime: this._u.uTime, uSafeCenter: safeZoneUniforms.uSafeCenter, uSafeHalfSize: safeZoneUniforms.uSafeHalfSize, uSafeFeather: safeZoneUniforms.uSafeFeather };
    this._displayMat = new THREE.ShaderMaterial({ uniforms: this._du, vertexShader: quadVertex, fragmentShader: displayFragment, depthTest: false, depthWrite: false });
    this._display = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), this._displayMat);
    this._display.frustumCulled = false; this._display.renderOrder = -1000;
    const self = this;
    this._size = new THREE.Vector2();
    this._display.onBeforeRender = function (renderer) { self._render(renderer); };
    scene.add(this._display);

    this._time = 0; this._free = 0; this._beats = 0; this._bars = 0; this._secondsBeat = 60 / 128;
    this._phaseBeat = null; this._phaseBar = null; this._phrasePhase = null; this._beatPhase = 0; this._barPhase = 0;
    this._low = 0.3; this._build = 0; this._rebuild = 1; this._flash = 0; this._waveT = 9;
    this._move = this._forceMove || 'flip'; this._prevMove = null; this._moveStart = 0; this._moveLen = 4 * this._secondsBeat;
    this._backVariant = 0; this._edges = this._forceEdges ? 1 : 0; this._camYaw = 0; this._camYawTarget = 0;
    this._coverUrl = undefined; this._coverTex = null; this._coverAspect = 1; this._jobId = 0; this._triCount = 0; this._workerMs = 0;
    this._paletteRef = undefined; this._palette = FALLBACK.slice(); this._palCols = FALLBACK.map((h) => vdjColor(h));
    this._makeFallbackCover();
  }

  _makeFallbackCover() {
    const n = 64, data = new Uint8Array(n * n * 4);
    for (let j = 0; j < n; j++) for (let i = 0; i < n; i++) {
      const x = (i + 0.5) / n - 0.5, y = (j + 0.5) / n - 0.5, r = Math.sqrt(x * x + y * y);
      const v = Math.max(0, 1 - r * 2.2);
      const o = (j * n + i) * 4; data[o] = 40 + 160 * v; data[o + 1] = 110 + 110 * v; data[o + 2] = 210; data[o + 3] = 255;
    }
    const t = new THREE.DataTexture(data, n, n, THREE.RGBAFormat, THREE.UnsignedByteType);
    t.colorSpace = THREE.SRGBColorSpace; t.needsUpdate = true;
    this._fallbackTex = t;
    this._useCover(t, 1);
  }
  // the cover pixels (a square cut, v up) to the worker; the mesh is rebuilt when the result arrives
  _useCover(tex, aspect) {
    this._coverAspect = aspect;
    const img = tex.image;
    if (!img || typeof document === 'undefined') return;
    let data;
    try {
      const cv = document.createElement('canvas'); cv.width = SRC; cv.height = SRC;
      const x = cv.getContext('2d');
      const w = img.width || SRC, h = img.height || SRC, s = Math.min(w, h);
      x.drawImage(img, (w - s) / 2, (h - s) / 2, s, s, 0, 0, SRC, SRC);
      const raw = x.getImageData(0, 0, SRC, SRC).data;
      data = new Uint8Array(SRC * SRC * 4);
      for (let j = 0; j < SRC; j++) data.set(raw.subarray((SRC - 1 - j) * SRC * 4, (SRC - j) * SRC * 4), j * SRC * 4);
      // the cover's mean luminance, for the comfort estimate
      let L = 0; for (let i = 0; i < raw.length; i += 16) L += 0.2126 * raw[i] + 0.7152 * raw[i + 1] + 0.0722 * raw[i + 2];
      this._coverLum = L / (raw.length / 16) / 255;
    } catch (e) { return; }
    const id = ++this._jobId;
    const done = (res) => { if (!this._scene || id !== this._jobId || !res || res.error || !res.tri) return; this._workerMs = res.ms || 0; this._area = res.area; this._npts = res.npts; this._buildMesh(res); };
    const w = sharedWorker();
    if (w) { _waiting.set(++_nextId, done); w.postMessage({ id: _nextId, w: SRC, data, count: this._count }, [data.buffer]); }
    else { try { done(triangulateOnMainThread(SRC, data, this._count)); } catch (e) { /* keep the previous mesh */ } }
  }
  // the one mesh: per triangle the top face and three prism sides, all attributes static
  _buildMesh(res) {
    const n = res.n, T = res.tri, C = res.col, K = res.coarse;
    const V = n * 21;                                   // 3 (top) + 3 sides x 2 triangles x 3 = 18 vertices per triangle
    // (sized at 9 at first: typed arrays drop out-of-range writes silently, and every triangle past the 512th vanished - black holes)
    const pos = new Float32Array(V * 3), cen = new Float32Array(V * 2), col = new Float32Array(V * 3), back = new Float32Array(V * 3), coarse = new Float32Array(V * 3);
    const nrm = new Float32Array(V * 3), rnd = new Float32Array(V * 3), bary = new Float32Array(V * 3), axA = new Float32Array(V * 2), axB = new Float32Array(V * 2), side = new Float32Array(V);
    const pal = this._palCols;
    let v = 0;
    const put = (x, y, z, f, b, sd, r0, r1, r2, nx, ny, ax, bx, cx, cy, by) => {
      pos[v * 3] = x; pos[v * 3 + 1] = y; pos[v * 3 + 2] = z;
      cen[v * 2] = cx; cen[v * 2 + 1] = cy;
      col[v * 3] = C[f * 3]; col[v * 3 + 1] = C[f * 3 + 1]; col[v * 3 + 2] = C[f * 3 + 2];
      coarse[v * 3] = K[f * 3]; coarse[v * 3 + 1] = K[f * 3 + 1]; coarse[v * 3 + 2] = K[f * 3 + 2];
      back[v * 3] = by.r; back[v * 3 + 1] = by.g; back[v * 3 + 2] = by.b;
      nrm[v * 3] = nx; nrm[v * 3 + 1] = ny; nrm[v * 3 + 2] = 1;
      rnd[v * 3] = r0; rnd[v * 3 + 1] = r1; rnd[v * 3 + 2] = r2;
      bary[v * 3] = b[0]; bary[v * 3 + 1] = b[1]; bary[v * 3 + 2] = b[2];
      axA[v * 2] = ax[0]; axA[v * 2 + 1] = ax[1]; axB[v * 2] = bx[0]; axB[v * 2 + 1] = bx[1];
      side[v] = sd; v++;
    };
    for (let f = 0; f < n; f++) {
      const p = [[T[f * 6], T[f * 6 + 1]], [T[f * 6 + 2], T[f * 6 + 3]], [T[f * 6 + 4], T[f * 6 + 5]]];
      // counter-clockwise winding (v up), for a consistent front face
      const cross = (p[1][0] - p[0][0]) * (p[2][1] - p[0][1]) - (p[1][1] - p[0][1]) * (p[2][0] - p[0][0]);
      if (cross < 0) { const t = p[1]; p[1] = p[2]; p[2] = t; }
      const cx = (p[0][0] + p[1][0] + p[2][0]) / 3, cy = (p[0][1] + p[1][1] + p[2][1]) / 3;
      const r0 = this._rnd(), r1 = this._rnd(), r2 = this._rnd();
      const nx = (r1 - 0.5) * 0.3, ny = (r2 - 0.5) * 0.3;
      const e = Math.floor(r0 * 3), ax = p[e], bx = p[(e + 1) % 3];
      // the back: a palette colour near the triangle's own hue, two variants by the phrase
      const by = this._backColour(C[f * 3], C[f * 3 + 1], C[f * 3 + 2], pal);
      const B = [[1, 0, 0], [0, 1, 0], [0, 0, 1]];
      for (let k = 0; k < 3; k++) put(p[k][0], p[k][1], 1, f, B[k], 0, r0, r1, r2, nx, ny, ax, bx, cx, cy, by);
      // the three sides: quads (two triangles) from the top edge (z = 1 -> h) down to the floor (z = 0)
      for (let k = 0; k < 3; k++) {
        const a = p[k], b = p[(k + 1) % 3];
        put(a[0], a[1], 1, f, [1, 1, 1], 1, r0, r1, r2, nx, ny, ax, bx, cx, cy, by);
        put(a[0], a[1], 0, f, [1, 1, 1], 1, r0, r1, r2, nx, ny, ax, bx, cx, cy, by);
        put(b[0], b[1], 1, f, [1, 1, 1], 1, r0, r1, r2, nx, ny, ax, bx, cx, cy, by);
        put(b[0], b[1], 1, f, [1, 1, 1], 1, r0, r1, r2, nx, ny, ax, bx, cx, cy, by);
        put(a[0], a[1], 0, f, [1, 1, 1], 1, r0, r1, r2, nx, ny, ax, bx, cx, cy, by);
        put(b[0], b[1], 0, f, [1, 1, 1], 1, r0, r1, r2, nx, ny, ax, bx, cx, cy, by);
      }
    }
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.BufferAttribute(pos, 3));
    g.setAttribute('aCentroid', new THREE.BufferAttribute(cen, 2));
    g.setAttribute('aColor', new THREE.BufferAttribute(col, 3));
    g.setAttribute('aBack', new THREE.BufferAttribute(back, 3));
    g.setAttribute('aCoarse', new THREE.BufferAttribute(coarse, 3));
    g.setAttribute('aNormal', new THREE.BufferAttribute(nrm, 3));
    g.setAttribute('aRand', new THREE.BufferAttribute(rnd, 3));
    g.setAttribute('aBary', new THREE.BufferAttribute(bary, 3));
    g.setAttribute('aAxisA', new THREE.BufferAttribute(axA, 2));
    g.setAttribute('aAxisB', new THREE.BufferAttribute(axB, 2));
    g.setAttribute('aSide', new THREE.BufferAttribute(side, 1));
    if (this._mesh) { this._priv.remove(this._mesh); this._geo.dispose(); }
    this._geo = g; this._mesh = new THREE.Mesh(g, this._mat); this._mesh.frustumCulled = false;
    this._priv.add(this._mesh);
    this._triCount = n;
  }
  _backColour(r, g, b, pal) {
    // the palette colour whose hue is nearest (variant 0) or furthest (variant 1), at the triangle's luminance
    const c = new THREE.Color(r, g, b), h = { h: 0, s: 0, l: 0 }; c.getHSL(h);
    let best = pal[0], bd = this._backVariant ? -1 : 9;
    for (const p of pal) { const ph = { h: 0, s: 0, l: 0 }; p.getHSL(ph); let d = Math.abs(ph.h - h.h); d = Math.min(d, 1 - d); if (this._backVariant ? d > bd : d < bd) { bd = d; best = p; } }
    const out = best.clone(); const bh = { h: 0, s: 0, l: 0 }; out.getHSL(bh);
    out.setHSL(bh.h, 0.85, 0.3 + 0.4 * h.l);
    return out;
  }
  _loadCover(profile) {
    if (this._debugCover) {
      if (this._coverUrl === this._debugCover) return;
      this._coverUrl = this._debugCover;
      const self = this;
      new THREE.TextureLoader().load(this._debugCover, (tex) => { if (!self._scene) { tex.dispose(); return; } self._coverTex = tex; const img = tex.image; self._useCover(tex, img && img.width ? img.width / img.height : 1); });
      return;
    }
    if (profile.artworkUrl === this._coverUrl) return;
    this._coverUrl = profile.artworkUrl;
    if (this._coverTex) { this._coverTex.dispose(); this._coverTex = null; }
    if (!profile.artworkUrl) { this._useCover(this._fallbackTex, 1); return; }
    const self = this, url = profile.artworkUrl;
    loadArtworkTexture(url, { wrap: THREE.ClampToEdgeWrapping, mipmaps: false }, (tex) => {
      if (!self._scene || url !== self._coverUrl) { tex.dispose(); return; }
      self._coverTex = tex; const img = tex.image; self._useCover(tex, img && img.width ? img.width / img.height : 1);
    }, () => {});
  }

  onTrackChange(profile) { this._loadCover(profile); }

  _nextMove() {
    if (this._forceMove) return this._forceMove;
    const pool = MOVES.filter((m) => m !== this._move && (this._layout.refract || m !== 'refract'));
    return pool[Math.floor(this._rnd() * pool.length)];
  }

  update(deltaTime, profile) {
    if (profile.artworkPalette !== this._paletteRef) {
      this._paletteRef = profile.artworkPalette;
      const v = profile.artworkPalette && profile.artworkPalette.vividColors && profile.artworkPalette.vividColors.length ? profile.artworkPalette.vividColors : FALLBACK;
      this._palette = v.slice(0, 4); this._palCols = this._palette.map((h) => vdjColor(h));
    }
    this._loadCover(profile);
    const playing = profile.playbackState === undefined || profile.playbackState === 'PLAYING';
    const dt = playing ? Math.min(deltaTime, 0.1) : 0;
    this._clock.update(dt);
    if (dt === 0) return;
    this._time += dt;
    const director = profile.director;
    const bands = (director && director.hasReliableGrid && director.bands) ? director.bands : null;
    const bpm = (profile.trackFeatures && profile.trackFeatures.bpm) || 0;
    this._secondsBeat = bpm > 0 ? 60 / bpm : 60 / 128;
    const barS = 4 * this._secondsBeat;
    let beat = false, bar = false, phrase = false;
    const bp = profile.beatPhase;
    if (bp != null) { if (this._phaseBeat != null && bp < this._phaseBeat - 0.5) beat = true; this._phaseBeat = bp; this._beatPhase = bp; }
    else { this._free += dt; if (this._free >= this._secondsBeat) { this._free -= this._secondsBeat; beat = true; } this._beatPhase = this._free / this._secondsBeat; }
    const barPhase = profile.barPhase;
    if (barPhase != null) { if (this._phaseBar != null && barPhase < this._phaseBar - 0.5) bar = true; this._phaseBar = barPhase; this._barPhase = barPhase; }
    else { if (beat && (this._beats + 1) % 4 === 0) bar = true; this._barPhase = ((this._beats % 4) + this._beatPhase) / 4; }
    const ph = profile.phrasePhase;
    if (ph != null) { if (this._phrasePhase != null && ph < this._phrasePhase - 0.5) phrase = true; this._phrasePhase = ph; }
    else if (beat && (this._beats + 1) % 16 === 0) phrase = true;
    if (beat) this._beats++;
    if (bar) this._bars++;
    const low = bands ? clamp01(bands.low) : Math.exp(-this._beatPhase * 6);
    this._low += (low - this._low) * Math.min(1, dt / 0.06);

    const next = director && director.nextEvent;
    let build = 0;
    if (next && next.type === 'drop' && next.msUntil < 8000) build = 1 - next.msUntil / 8000;
    if (director && director.mode === 'build') build = Math.max(build, 0.6);
    this._build += (build - this._build) * (1 - Math.exp(-dt / 0.8));
    const dropNow = next && next.type === 'drop' && next.msUntil != null && next.msUntil <= dt * 500 + 30 && profile.estimatedPosition != null;
    if (dropNow) {
      const key = Math.round((profile.estimatedPosition * 1000 + next.msUntil) / 100);
      if (key !== this._lastDropKey) {
        this._lastDropKey = key;
        this._rebuild = 0; this._flash = 1;
        if (director && this._comfort.requestFlash(this._time)) director.flash = VDJ_DROP_FLASH;
      }
    }
    if (this._rebuild < 1) this._rebuild = Math.min(1, this._rebuild + dt / this._secondsBeat);
    this._flash *= Math.pow(0.5, dt / 0.07);

    // BAR: the next move (the automaton); PHRASE: the back palette's variant, the edges
    if (bar || (this._clock.wrapped && this._prevMove == null)) {
      this._prevMove = this._move; this._move = this._nextMove(); this._moveStart = this._time; this._moveLen = barS;
      this._u.uFlipFrom.value.set(this._rnd() < 0.5 ? 0 : 1, this._rnd() < 0.5 ? 0 : 1);
      this._camYawTarget = this._move === 'extrude' ? (this._rnd() < 0.5 ? -1 : 1) * 20 * Math.PI / 180 : 0;
    }
    if (phrase) { this._backVariant = 1 - this._backVariant; if (!this._forceEdges) this._edges = this._rnd() < 0.5 ? 1 : 0; }
    // the move's envelope over its bar: up over the first half, back over the second (the flip runs 0 -> 1 across)
    const mt = clamp01((this._time - this._moveStart) / Math.max(0.3, this._moveLen));
    const upDown = Math.sin(mt * Math.PI);
    const U = this._u;
    const m = this._move;
    U.uFlip.value = m === 'flip' ? mt : 0;
    U.uExtrude.value = m === 'extrude' ? upDown : 0;
    U.uScatter.value = m === 'scatter' ? upDown * upDown : 0;
    U.uRefract.value = m === 'refract' && this._layout.refract ? upDown : 0;
    U.uSimplify.value = Math.max(m === 'simplify' ? upDown : 0, this._build * 0.9);
    U.uMoveK.value = Math.max(upDown, this._build);
    U.uRebuild.value = this._rebuild;
    U.uLow.value = this._low;
    U.uTime.value = this._time;
    U.uEdges.value = this._edges;
    U.uFlash.value = this._flash;
    U.uWidth.value = HEIGHT * Math.min(1.6, Math.max(0.7, this._coverAspect));
    // the light: a slow turn; the beat's wave sweeps the mosaic in half a beat
    const la = this._time * 0.35;
    U.uLightDir.value.set(Math.cos(la) * 0.6, 0.5, 0.6 + 0.3 * Math.sin(la * 0.7)).normalize();
    if (beat) this._waveT = 0;
    this._waveT += dt;
    U.uWave.value = this._waveT / (0.5 * this._secondsBeat) * 2.4 - 0.2;
    this._post.update(vdjBeatChroma(this._beatPhase, this._secondsBeat));
    // comfort (R10): the cover's mean luminance x the current gain (the governor expects a post-gain measure)
    const gain = this._comfort.measure((this._coverLum || 0.35) * 0.9 * this._comfort.gain + this._flash * 0.4, dt);
    U.uExposure.value = gain;
    // the camera: frontal, a 20 deg orbit for the extrusion, a slight sway
    this._camYaw += (this._camYawTarget - this._camYaw) * (1 - Math.exp(-dt / 0.6));
    const cam = this._camera, R = 4.9, yaw = this._camYaw + 0.03 * Math.sin(this._time * 0.4);
    cam.position.set(Math.sin(yaw) * R, 0.12 * Math.sin(this._time * 0.27), Math.cos(yaw) * R);
    cam.up.set(0, 1, 0); cam.lookAt(0, 0, 0); cam.fov = 32; cam.updateProjectionMatrix();
  }

  _render(renderer) {
    renderer.getDrawingBufferSize(this._size);
    const W = Math.max(2, this._size.x), H = Math.max(2, this._size.y);
    if (!this._rt || this._rt.width !== W || this._rt.height !== H) {
      if (this._rt) this._rt.dispose();
      this._rt = new THREE.WebGLRenderTarget(W, H, { samples: this._layout.msaa, depthBuffer: true, type: THREE.UnsignedByteType, minFilter: THREE.LinearFilter, magFilter: THREE.LinearFilter, generateMipmaps: false });
      this._du.uTex.value = this._rt.texture;
    }
    const cam = this._camera; cam.aspect = W / H; cam.updateProjectionMatrix();
    const prev = renderer.getRenderTarget(), auto = renderer.autoClear, cc = renderer.getClearColor(new THREE.Color()), ca = renderer.getClearAlpha();
    renderer.setRenderTarget(this._rt); renderer.setClearColor(0x000000, 1); renderer.autoClear = true;
    renderer.render(this._priv, cam);
    renderer.setRenderTarget(prev); renderer.autoClear = auto; renderer.setClearColor(cc, ca);
  }

  get probe() {
    return { t: +this._time.toFixed(2), move: this._move, prev: this._prevMove, mt: +clamp01((this._time - this._moveStart) / Math.max(0.3, this._moveLen)).toFixed(2), tris: this._triCount, area: +(this._area || 0).toFixed(4), pts: this._npts || 0, workerMs: +this._workerMs.toFixed(0),
      edges: this._edges, back: this._backVariant, build: +this._build.toFixed(2), rebuild: +this._rebuild.toFixed(2), gain: +this._comfort.gain.toFixed(3), yaw: +(this._camYaw * 180 / Math.PI).toFixed(1), cover: !!this._coverTex, worker: !!_worker, bars: this._bars };
  }

  dispose() {
    this._jobId++;
    if (this._display) { this._display.onBeforeRender = null; if (this._scene) this._scene.remove(this._display); this._display.geometry.dispose(); this._displayMat.dispose(); }
    if (this._geo) this._geo.dispose();
    if (this._mat) this._mat.dispose();
    if (this._rt) this._rt.dispose();
    if (this._coverTex) this._coverTex.dispose();
    if (this._fallbackTex) this._fallbackTex.dispose();
    this._scene = null;
  }
}
